const DNI_REGEX = /^(\d{8})([A-Z])$/;
const CIF_REGEX = /^([ABCDEFGHJKLMNPQRSUVW])(\d{7})([0-9A-J])$/;
const NIE_REGEX = /^[XYZ]\d{7,8}[A-Z]$/;
const DNI_LETTERS = 'TRWAGMYFPDXBNJZSQVHLCKE';
const CIF_LETTERS: string = 'JABCDEFGHI';

export enum SPANISH_ID_TYPES {
  DNI = 'dni',
  NIE = 'nie',
  CIF = 'cif',
  INVALID = 'invalid',
}

const spainIdType = function (str: string) {
  if (str.match(DNI_REGEX)) {
    return SPANISH_ID_TYPES.DNI;
  }
  if (str.match(CIF_REGEX)) {
    return SPANISH_ID_TYPES.CIF;
  }
  if (str.match(NIE_REGEX)) {
    return SPANISH_ID_TYPES.NIE;
  }

  return SPANISH_ID_TYPES.INVALID;
};

export const validateVatID = (vat: string): boolean => {
  const CIF_LETTER_MAP = 'JABCDEFGHI';
  // must start with ES
  if (!vat.startsWith('ES')) return false;
  const code = vat.slice(2);

  // --- NIF branch (personal) ---
  // 7 or 8 digits + check-letter
  const nifRe = /^(\d{7,8})([TRWAGMYFPDXBNJZSQVHLCKE])$/;
  let m = code.match(nifRe);
  if (m) {
    let [_, numPart, checkLetter] = m;
    // pad to 8 digits if only 7 provided
    if (numPart.length === 7) numPart = '0' + numPart;
    return DNI_LETTERS.charAt(parseInt(numPart, 10) % 23) === checkLetter;
  }

  // --- CIF branch (company) ---
  // entity-letter + 7 digits + control (digit or letter)
  const cifRe = /^([ABCDEFGHJNPQRSUVW])(\d{7})([A-Z0-9])$/;
  m = code.match(cifRe);
  if (m) {
    const [_, type, digits7, controlChar] = m;
    const digits = digits7.split('').map((d) => +d);

    // 1) sum of digits in even positions (2,4,6)
    const sumEven = digits[1] + digits[3] + digits[5];

    // 2) sum of digits of (odd‐position ×2)
    const sumOdd = digits
      .filter((_, i) => i % 2 === 0)
      .map((n) =>
        (n * 2)
          .toString()
          .split('')
          .reduce((s, d) => s + +d, 0),
      )
      .reduce((a, b) => a + b, 0);

    const total = sumEven + sumOdd;
    const digitCtrl = (10 - (total % 10)) % 10;
    const letterCtrl = CIF_LETTER_MAP.charAt(digitCtrl);

    // numeric-only: A, B, E, H
    if ('ABEH'.includes(type)) return controlChar === String(digitCtrl);
    // letter-only: P, Q, S, N, W
    if ('PQSNW'.includes(type)) return controlChar === letterCtrl;
    // can be either
    return controlChar === letterCtrl || controlChar === String(digitCtrl);
  }

  // neither NIF nor CIF
  return false;
};

const validDNI = (dni: string) => {
  const letter = DNI_LETTERS.charAt(parseInt(dni, 10) % 23);

  return letter === dni.charAt(8);
};

const validNIE = (nie: string) => {
  // Change the initial letter for the corresponding number and validate as DNI
  let nie_prefix: string = nie.charAt(0);

  switch (nie_prefix) {
    case 'X':
      nie_prefix = '0';
      break;
    case 'Y':
      nie_prefix = '1';
      break;
    case 'Z':
      nie_prefix = '2';
      break;
  }

  return validDNI(nie_prefix + nie.substring(1));
};

const validCIF = (cif: string): boolean => {
  const match = cif.match(CIF_REGEX);
  if (match) {
    const letter = match[1],
      number = match[2],
      control = match[3];

    let even_sum = 0;
    let odd_sum = 0;
    let n;

    // Add the digits of the even positions. Sum = A
    // For each of the digits of the odd positions, multiply it by 2 and add the digits of the result. For example .: (8 * 2 = 16 -> 1 + 6 = 7). Accumulate the result. Sum = B
    // Calculate the sum A + B = C
    // Take only the last digit of the units of C and subtract it from 10. This result of this subtraction gives us D.
    //     From D the control digit is already obtained. If it is to be numeric it is directly D and if it is a letter it corresponds to the relation:
    // A = 1, B = 2, C = 3, D = 4, E = 5, F = 6, G = 7, H = 8, I = 9, J = 10 or 0
    for (let i = 0; i < number.length; i++) {
      n = parseInt(number[i], 10);

      // Odd positions (Even index equals to odd position. i=0 equals first position)
      if (i % 2 === 0) {
        // Odd positions are multiplied first.
        n *= 2;
        if (n > 9) {
          n = Math.floor(n / 10) + (n % 10);
        }

        odd_sum += n;

        // Even positions
      } else {
        even_sum += n; // Just sum them
      }
    }

    const total = even_sum + odd_sum;

    let control_digit: number = 10 - (total % 10);
    control_digit = control_digit === 10 ? 0 : control_digit;
    const control_letter: string = getCifControlLetter(control_digit); // CIF_LETTERS.substring(control_digit)[0];

    console.log({ control_digit, control_letter });

    // Control must be a digit
    if (letter.match(/[ABEH]/)) {
      return control === `${control_digit}`;

      // Control must be a letter
    } else if (letter.match(/[KPQS]/)) {
      return control === control_letter;

      // Can be either
    } else {
      return control === `${control_digit}` || control === control_letter;
    }
  }

  return false;
};

const getCifControlLetter = (control_digit: number) => {
  return CIF_LETTERS.substring(control_digit)[0];
};

/**
 * ValidateSpanishID. Returns the type of document and checks its validity.
 *
 * Usage:
 *     ValidateSpanishID( str );
 *
 *     > ValidateSpanishID( '12345678Z' );
 *     // { type: 'dni', valid: true }
 *
 *     > ValidateSpanishID( 'B83375575' );
 *     // { type: 'cif', valid: false }
 *
 * The algorithm is adapted from other solutions found at:
 * - http://www.compartecodigo.com/javascript/validar-nif-cif-nie-segun-ley-vigente-31.html
 * - http://es.wikipedia.org/wiki/C%C3%B3digo_de_identificaci%C3%B3n_fiscal
 */
export const ValidateSpanishID = (str: string) => {
  // Ensure uppercase and remove whitespace
  str = str.toUpperCase().replace(/\s/, '');

  let valid = false;
  const type: SPANISH_ID_TYPES = spainIdType(str);

  if (type !== SPANISH_ID_TYPES.INVALID) {
    switch (type) {
      case SPANISH_ID_TYPES.DNI:
        valid = validDNI(str);
        break;
      case SPANISH_ID_TYPES.NIE:
        valid = validNIE(str);
        break;
      case SPANISH_ID_TYPES.CIF:
        valid = validCIF(str);
        break;
    }

    return {
      type,
      valid,
    };
  }

  return {
    type,
    valid: false,
  };
};

export interface INifValidationResult {
  type: SPANISH_ID_TYPES;
  valid: boolean;
}
